/**
 * Register web components used in the application here as a side effect import.
 * This ensures that the components are not tree-shaken away during the build process
 * even when no other typeScript file imports them.
 */
import {
	AddisonEAU,
	AddisonEAUConfigDialog,
	AddisonSync,
	AddisonSyncConfigDialog,
	BMD,
	BmdConfigDialog,
	CustomIntegration, Google,
	Jira,
	Microsoft,
	Personio,
	PersonioConfigDialog,
	Slack
} from "./pages/admin/settings/panels";
import {FreeModuleSelectionDialog} from "./pages/admin/account/freeModuleSelectionDialog";
import {LicensedUserDialog} from "./pages/admin/pricing/licensedUsersDialog";
import {ExtendRuntimeDialog} from "./pages/admin/pricing/extendRuntimeDialog";
import {AccountOnboardingWidget} from "./pages/onboarding/accountOnboardingWidget";
import {AppOnboardingWidget} from "./pages/onboarding/appOnboardingWidget";
import {FeatureModulesOnboardingWidget} from "./pages/onboarding/featureModulesOnboardingWidget";
import {HelpCenterOnboardingWidget} from "./pages/onboarding/helpCenterOnboardingWidget";
import {TerminalOnboardingWidget} from "./pages/onboarding/terminalOnboardingWidget";
import {SupportOnboardingWidget} from "./pages/onboarding/supportOnboardingWidget";
import {WelcomeOnboardingWidget} from "./pages/onboarding/welcomeOnboardingWidget";
import {WorkScheduleWeekComponent} from "./pages/admin/workScheduleModel/workScheduleWeekComponent";
import {WorkScheduleModelWeekComponent} from "./pages/admin/workScheduleModel/workScheduleModelWeekComponent";
import {HolidayIndividualDays} from "./features/holidays/holidayIndividualDays";
import {TimelineWeekView} from "./features/timeline/views/timelineWeekView";
import {TimelineDaysView} from "./features/timeline/views/timelineDaysView";
import {TimelineWeekCutDayIndicators} from "./features/timeline/views/timelineWeekCutDayIndicators";
import {CombinedTimelineDayValidations} from "./features/timeline/combined/combinedTimelineDayValidations";
import {CombinedTimelineSubitems} from "./features/timeline/combined/combinedTimelineSubitems";
import {CombinedTimelineDay} from "./features/timeline/combined/combinedTimelineDay";
import {CombinedTimelineDayHoliday} from "./features/timeline/combined/combinedTimelineDayHoliday";
import {CombinedTimelineDayRequests} from "./features/timeline/combined/combinedTimelineDayRequests";
import {CombinedTimelineDayBasedWorkingTimes} from "./features/timeline/combined/combinedTimelineDayBasedWorkingTimes";
import {CombinedTimelineDayNotices} from "./features/timeline/combined/combinedTimelineDayNotices";
import {CombinedTimelineDayTitle} from "./features/timeline/combined/combinedTimelineDayTitle";
import {CombinedTimelineDayItems} from "./features/timeline/combined/combinedTimelineDayItems";
import {ProjectTimeTimelineDay} from "./features/timeline/projecttime/projectTimeTimelineDay";
import {ProjectTimeTimelineDayTitle} from "./features/timeline/projecttime/projectTimeTimelineDayTitle";
import {ProjectTimeTimelineDayItems} from "./features/timeline/projecttime/projectTimeTimelineDayItems";
import {ProjectTimeTimelineDayValidations} from "./features/timeline/projecttime/projectTimeTimelineDayValidations";
import {ProjectTimeTimelineDayHoliday} from "./features/timeline/projecttime/projectTimeTimelineDayHoliday";
import {PauseCombinedRecordingDialog} from "./features/timeline/pauseCombinedRecordingDialog";
import {QrCodeScannerDialog} from "@/features/user/qrCodeScannerDialog";
import {WorkingTimeRequestStepsComponent} from "@/features/requests/workingTimeRequestStepsComponent";
import {WorkingTimeRequestStepsConfigurationComponent} from "@/features/requests/workingTimeRequestStepsConfigurationComponent";
import {TaskSelectionConfigurationComponent} from "@/features/terminal/taskSelectionConfigurationComponent";
import {CreditabilityCompensationComponent} from "./pages/admin/workingTimeType/creditabilityCompensationComponent";
import {FeatureModuleItem} from "./features/pricing/featureModuleItem";
import {FeatureModuleSelectionDialog} from "./features/pricing/featureModuleSelectionDialog";
import {FeatureModuleSelectionUserInput} from "./features/pricing/featureModuleSelectionUserInput";
import {TrialFeatureModuleSelectionDialog} from "./features/pricing/trialFeatureModuleSelectionDialog";
import {AttachmentGallery} from "@/shared/ui/attachmentGallery";
import {ValidationsCollapseComponent} from "@/features/validations/validationsCollapseComponent";
import {ValidationsListDialogComponent} from "@/shared/validations/validationsListComponent";
import {ResetTrialDataDialog} from "@/features/trial/resetTrialDataDialog";
import {SubstituteSelection} from "@/shared/workingtime/substituteSelection";
import {SubstituteValidation} from "@/shared/workingtime/substituteValidation";
import {ProofAttachmentGallery} from "@/shared/workingtime/proofAttachmentGallery";
import {LocationPanel} from "@/shared/locationPanel";

// admin/settings
customElements.define("banner-addison", AddisonEAU);
customElements.define("addison-config-dialog", AddisonEAUConfigDialog);
customElements.define("banner-addison_sync", AddisonSync);
customElements.define("addison_sync-config-dialog", AddisonSyncConfigDialog);
customElements.define("banner-jira", Jira);
customElements.define("banner-personio", Personio);
customElements.define("personio-config-dialog", PersonioConfigDialog);
customElements.define("banner-slack", Slack);
customElements.define("banner-custom", CustomIntegration);
customElements.define("banner-bmd", BMD);
customElements.define("bmd-config-dialog", BmdConfigDialog);
customElements.define("banner-google", Google);
customElements.define("banner-microsoft", Microsoft);

// admin/workScheduleModel
customElements.define("work-schedule-week", WorkScheduleWeekComponent);
customElements.define("work-schedule-model-week", WorkScheduleModelWeekComponent);

// admin/pricing
customElements.define("free-module-selection-dialog", FreeModuleSelectionDialog);
customElements.define("licensed-users-dialog", LicensedUserDialog);
customElements.define("extend-runtime-dialog", ExtendRuntimeDialog);

// pages/admin/workingTimeType
customElements.define("creditability-compensation-component", CreditabilityCompensationComponent);

// Onboarding dashboard
customElements.define("onboarding-widget-account", AccountOnboardingWidget);
customElements.define("onboarding-widget-connect-app", AppOnboardingWidget);
customElements.define("onboarding-widget-feature-modules", FeatureModulesOnboardingWidget);
customElements.define("onboarding-widget-help-center", HelpCenterOnboardingWidget);
customElements.define("onboarding-widget-terminal", TerminalOnboardingWidget);
customElements.define("onboarding-widget-support", SupportOnboardingWidget);
customElements.define("onboarding-widget-welcome", WelcomeOnboardingWidget);

// Holidays
customElements.define("holiday-individual-days", HolidayIndividualDays);

// features/user
customElements.define("qr-code-scanner-dialog", QrCodeScannerDialog);

// features/requests
customElements.define("working-time-request-steps-configuration-component", WorkingTimeRequestStepsConfigurationComponent);
customElements.define("working-time-request-steps", WorkingTimeRequestStepsComponent);

// shared/webcomponents
customElements.define("attachment-gallery", AttachmentGallery);
customElements.define("validations-collapse", ValidationsCollapseComponent);
customElements.define("validations-list-dialog", ValidationsListDialogComponent);
customElements.define("reset-trial-data-dialog", ResetTrialDataDialog);
customElements.define("substitute-selection", SubstituteSelection);
customElements.define("substitute-validation", SubstituteValidation);
customElements.define("proof-attachment-gallery", ProofAttachmentGallery);
customElements.define("location-panel", LocationPanel);

// features/terminal
customElements.define("timr-task-selection-configuration", TaskSelectionConfigurationComponent);

// Pricing
customElements.define("feature-module-item", FeatureModuleItem);
customElements.define("feature-module-selection-dialog", FeatureModuleSelectionDialog);
customElements.define("feature-module-selection-user-input", FeatureModuleSelectionUserInput);
customElements.define("trial-feature-module-selection-dialog", TrialFeatureModuleSelectionDialog);

// timeline
customElements.define("timeline-week", TimelineWeekView);
customElements.define("timeline-days", TimelineDaysView);
customElements.define("timeline-week-cut-day-indicators", TimelineWeekCutDayIndicators);
customElements.define("combined-timeline-day-validations", CombinedTimelineDayValidations);
customElements.define("combined-timeline-subitems", CombinedTimelineSubitems);
customElements.define("combined-timeline-day", CombinedTimelineDay);
customElements.define("combined-timeline-day-holiday", CombinedTimelineDayHoliday);
customElements.define("combined-timeline-day-requests", CombinedTimelineDayRequests);
customElements.define("combined-timeline-day-based-working-times", CombinedTimelineDayBasedWorkingTimes);
customElements.define("combined-timeline-day-notices", CombinedTimelineDayNotices);
customElements.define("combined-timeline-day-title", CombinedTimelineDayTitle);
customElements.define("combined-timeline-day-items", CombinedTimelineDayItems);
customElements.define("project-time-timeline-day", ProjectTimeTimelineDay);
customElements.define("project-time-timeline-day-title", ProjectTimeTimelineDayTitle);
customElements.define("project-time-timeline-day-items", ProjectTimeTimelineDayItems);
customElements.define("project-time-timeline-day-validations", ProjectTimeTimelineDayValidations);
customElements.define("project-time-timeline-day-holiday", ProjectTimeTimelineDayHoliday);
customElements.define("pause-combined-recording-dialog", PauseCombinedRecordingDialog);
